import React, { FC, Fragment, useMemo } from "react";
import styled from "styled-components";

import { vwMobile, vwDesktop, rem } from "../../helpers/styles";
import { BREAKPOINTS } from "../../helpers/theme";
import { Image } from "../../types";
import Card from "./Card";
import { useAppSelector } from "../../hooks/useAppSelector";
import { LOCALES } from "../../helpers/locales";

export type ContactUsProps = {
  title: string;
  simpleCards: SimpleCard[];
  card4: Card4;
  card5: Card5;
};

type SimpleCard = {
  isPublished: boolean;
  image: Image;
  title: string;
  description: string;
  button: { label: string; link: string } | null;
};

type Card4 = {
  isPublished: boolean;
  image: Image;
  description: { email: string; name: string }[];
  button: { label: string; link: string };
};

type Card5 = {
  isPublished: boolean;
  image: Image;
  title: string;
  description: string;
};

const ContactUs: FC<ContactUsProps> = ({
  title,
  simpleCards,
  card4,
  card5,
}: ContactUsProps) => {
  const locale = useAppSelector((state) => state.locale.locale);

  const reOrderedSimpleCards = useMemo(() => {
    if (locale === LOCALES.EN_GB) {
      const usCaForm = simpleCards?.splice(1, 1) || [];
      return [...simpleCards, ...usCaForm]?.filter(
        ({ isPublished }) => isPublished
      );
    }
    return simpleCards?.filter(({ isPublished }) => isPublished);
  }, [simpleCards, locale]);

  return (
    <Container>
      <Title
        dangerouslySetInnerHTML={{
          __html: title,
        }}
      />
      <CardWrapper>
        <TopCardWrapper>
          {reOrderedSimpleCards?.map((card, index) => (
            <Card key={index} image={card?.image} button={card?.button}>
              <>
                <CardTitle>{card?.title}</CardTitle>
                <CardDescription>{card?.description}</CardDescription>
              </>
            </Card>
          ))}
        </TopCardWrapper>
        <BottomCardWrapper>
          {card4?.isPublished && (
            <Card image={card4?.image} button={card4?.button}>
              <>
                {card4?.description?.map(
                  (option: { email: string; name: string }, index: number) => (
                    <Fragment key={index}>
                      <CardTitle>{option?.name}</CardTitle>
                      <CardSubtitle>{option?.email}</CardSubtitle>
                    </Fragment>
                  )
                )}
              </>
            </Card>
          )}
          {card5?.isPublished && (
            <Card image={card5?.image}>
              <>
                <CardTitle>{card5?.title}</CardTitle>
                <LocationContent
                  dangerouslySetInnerHTML={{
                    __html: card5?.description,
                  }}
                />
              </>
            </Card>
          )}
        </BottomCardWrapper>
      </CardWrapper>
    </Container>
  );
};

const Container = styled.section`
  background-color: #033305;
  padding: ${vwMobile(40)} ${vwMobile(20)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding: ${vwDesktop(40)} ${vwDesktop(100)} ${vwDesktop(68)};
  }
`;

const Title = styled.h1`
  color: #ffffff;
  font-family: ${props => props.theme.fontFamily}, ${props => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${rem(44)};
  font-weight: 900;
  letter-spacing: 0.012em;
  line-height: ${rem(53)};
  margin-bottom: ${vwMobile(40)};
  text-transform: uppercase;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    font-size: ${rem(80)};
    letter-spacing: 0.03em;
    line-height: ${rem(88)};
    margin-bottom: ${vwDesktop(29)};
  }

  strong {
    color: #75c154;
    font-weight: inherit;
  }
`;

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${vwMobile(40)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    gap: ${vwDesktop(20)};
  }
`;

const TopCardWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${vwMobile(40)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${vwDesktop(20)};
  }
`;

const BottomCardWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${vwMobile(40)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${vwDesktop(20)};

    & > :first-child {
      grid-column: 1 / 3;
    }
  }
`;

const CardTitle = styled.h2`
  color: #ffffff;
  font-family: ${props => props.theme.fontFamily}, ${props => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${rem(36)};
  font-weight: 900;
  letter-spacing: 0.03em;
  line-height: ${rem(40)};
  margin-bottom: ${vwMobile(4)};
  text-transform: uppercase;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    margin-bottom: ${vwDesktop(4)};
  }
`;

const CardSubtitle = styled.span`
  color: #75c154;
  font-family: ${props => props.theme.fontFamily}, ${props => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${rem(18)};
  font-weight: 900;
  letter-spacing: 0.06em;
  line-height: ${rem(24)};
  margin-bottom: ${vwMobile(20)};
  text-transform: uppercase;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    margin-bottom: ${vwDesktop(20)};
  }
`;

const CardDescription = styled.p`
  color: #ffffff;
  font-family: ${props => props.theme.fontFamily}, ${props => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${rem(18)};
  font-weight: 600;
  letter-spacing: 0;
  margin-bottom: ${vwMobile(20)};
  line-height: ${rem(28)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    margin-bottom: ${vwDesktop(20)};
  }
`;

const LocationContent = styled.p`
  color: #ffffff;
  font-family: ${props => props.theme.fontFamily}, ${props => props.theme.fallBackFontFamily}, sans-serif;
  margin-top: ${vwMobile(20)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    margin-top: ${vwDesktop(20)};
  }

  h3 {
    font-size: ${rem(24)};
    font-weight: 900;
    letter-spacing: 0.06em;
    line-height: ${rem(31)};
    text-transform: uppercase;
  }

  p {
    font-size: ${rem(18)};
    font-weight: 600;
    letter-spacing: 0;
    margin-bottom: ${vwMobile(20)};
    line-height: ${rem(28)};

    @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
      margin-bottom: ${vwDesktop(20)};
    }
  }
`;

export default ContactUs;
